import React, { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Loader2, AlertCircle } from "lucide-react";
import { chatbotQuery, getSuggestedQuestions } from "../lib/chatbot";
import { toast } from "sonner";

export default function ChatbotWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Initialize with welcome message when opening
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([
        {
          role: "assistant",
          content: "👋 Hi! I'm the Orello support assistant. How can I help you today?",
          timestamp: new Date(),
        },
      ]);
    }
  }, [isOpen, messages.length]);

  const handleSendMessage = async () => {
    if (!input.trim()) return;

    const userMessage = {
      role: "user",
      content: input,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setLoading(true);
    setError(null);

    try {
      const response = await chatbotQuery(input, conversationId, null);

      if (!conversationId) {
        setConversationId(response.conversation_id);
      }

      const assistantMessage = {
        role: "assistant",
        content: response.answer,
        timestamp: new Date(),
        suggestedActions: response.suggested_actions,
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (err) {
      console.error("Chat error:", err);
      setError("Failed to get a response. Please try again.");

      // Remove the user message on error
      setMessages((prev) => prev.slice(0, -1));

      // Show error toast
      const errorMsg = err.response?.data?.detail || "Failed to send message";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const suggestedQuestions = getSuggestedQuestions();

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 right-6 h-14 w-14 rounded-full bg-[#F97316] text-white shadow-lg hover:shadow-xl hover:scale-110 transition-all flex items-center justify-center z-40 ${
          isOpen ? "hidden" : "flex"
        }`}
        title="Chat with support"
        aria-label="Open chat"
      >
        <MessageCircle className="h-6 w-6" />
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div
          className="fixed inset-0 z-50 md:bottom-6 md:right-6 md:w-96 md:h-[600px] md:inset-auto md:rounded-2xl md:shadow-xl bg-white flex flex-col"
          style={{ maxHeight: "100vh" }}
        >
          {/* Header */}
          <div className="bg-[#F97316] text-white p-4 rounded-t-2xl md:rounded-t-2xl flex items-center justify-between sticky top-0">
            <div>
              <h3 className="font-semibold text-white">Orello Support</h3>
              <p className="text-xs text-orange-100">Always here to help</p>
            </div>
            <button
              onClick={handleClose}
              className="text-white hover:bg-orange-700 rounded-full p-1 transition-colors"
              aria-label="Close chat"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Messages Container */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <MessageCircle className="h-12 w-12 text-gray-300 mb-3" />
                <p className="text-gray-600 text-sm mb-4">Ask me anything about Orello!</p>
                <div className="space-y-2 w-full">
                  <p className="text-xs text-gray-500 font-semibold uppercase">Popular questions:</p>
                  {suggestedQuestions.slice(0, 3).map((q, i) => (
                    <button
                      key={i}
                      onClick={() => {
                        setInput(q);
                      }}
                      className="w-full text-left text-xs p-2 rounded bg-white hover:bg-orange-50 text-gray-700 border border-gray-200 transition-colors"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <>
                {messages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-xs px-3 py-2 rounded-lg text-sm ${
                        msg.role === "user"
                          ? "bg-[#F97316] text-white rounded-br-none"
                          : "bg-white text-gray-800 border border-gray-200 rounded-bl-none"
                      }`}
                    >
                      <p className="whitespace-pre-wrap">{msg.content}</p>
                      {msg.role === "assistant" && msg.suggestedActions && (
                        <div className="mt-2 pt-2 border-t border-gray-200 flex flex-wrap gap-1">
                          {msg.suggestedActions.slice(0, 2).map((action, i) => (
                            <button
                              key={i}
                              className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors"
                              onClick={() => {
                                if (action === "Contact support") {
                                  // Open email client
                                  window.location.href = "mailto:support@orello.in?subject=Support Request from Orello Chatbot";
                                } else if (action === "Escalate to support") {
                                  setInput("I'd like to escalate this to human support");
                                } else {
                                  setInput(action);
                                }
                              }}
                            >
                              {action}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {loading && (
                  <div className="flex justify-start">
                    <div className="bg-white text-gray-800 border border-gray-200 rounded-lg rounded-bl-none px-3 py-2 flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-sm text-gray-600">Thinking...</span>
                    </div>
                  </div>
                )}

                {error && (
                  <div className="flex justify-start">
                    <div className="bg-red-50 border border-red-200 rounded-lg rounded-bl-none px-3 py-2 flex items-start gap-2 max-w-xs">
                      <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-red-700">{error}</span>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* Input Area */}
          <div className="border-t border-gray-200 p-3 bg-white rounded-b-2xl md:rounded-b-2xl sticky bottom-0">
            <div className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask your question..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F97316] focus:border-transparent"
                disabled={loading}
              />
              <button
                onClick={handleSendMessage}
                disabled={loading || !input.trim()}
                className="bg-[#F97316] text-white p-2 rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                aria-label="Send message"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">💡 Tip: Ask about features, pricing, KYC, or payments</p>
          </div>
        </div>
      )}
    </>
  );
}
